import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { Card, HStack, VStack } from '@components/base/layout';
import { Spinner } from '@components/base/feedback';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { workflowStepsService } from '../../api/workflowStepsService';
import { workflowRulesService } from '../../api/workflowRulesService';
import type { WorkflowStep } from '../../api/workflowStepsService';
import type { WorkflowRuleTenant } from '../../api/workflowRulesService';

// ---------------------------------------------------------------------------
// Step row renderer (shared between generic and effective tables)
// ---------------------------------------------------------------------------

function StepRow({ step, genericStepIds }: Readonly<{ step: WorkflowStep; genericStepIds?: Set<string> }>) {
  const iterateGroup = step.config?.iterate_group as string | undefined;
  const isReplaced = genericStepIds != null && genericStepIds.has(step.id) === false;

  return (
    <>
      <Table.Cell>
        <Text size="sm" color="muted">
          {step.sort_order}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <HStack gap={2} align="center">
          <Text weight="medium">{step.name}</Text>
          {isReplaced && (
            <Badge variant="warning" size="sm">replaced</Badge>
          )}
        </HStack>
      </Table.Cell>
      <Table.Cell>
        <Badge variant="info" size="sm">
          {step.step_type}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        {iterateGroup ? (
          <Badge variant="warning" size="sm">
            {iterateGroup}
          </Badge>
        ) : (
          <Text size="sm" color="muted">{'\u2014'}</Text>
        )}
      </Table.Cell>
      <Table.Cell>
        <Badge
          variant={step.execution_mode === 'async' ? 'warning' : 'default'}
          size="sm"
        >
          {step.execution_mode}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        <Badge
          variant={step.is_active ? 'success' : 'default'}
          size="sm"
        >
          {step.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        <Text size="sm" color="muted">
          {step.timeout_seconds == null ? '\u2014' : `${step.timeout_seconds}s`}
        </Text>
      </Table.Cell>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tenant effective steps table
// ---------------------------------------------------------------------------

function TenantEffectiveStepsTable({
  workflowSlug,
  tenant,
  genericStepIds,
}: Readonly<{
  workflowSlug: string;
  tenant: WorkflowRuleTenant;
  genericStepIds: Set<string>;
}>) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'workflows', workflowSlug, 'effective-steps', tenant.id],
    queryFn: () => workflowRulesService.getEffectiveSteps(workflowSlug, tenant.id),
  });

  const steps = useMemo(() => {
    const raw = data?.data ?? [];
    return [...raw].sort((a, b) => a.sort_order - b.sort_order);
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <Card.Header title={tenant.name} />
        <Card.Content>
          <VStack gap={2} align="center" className="py-6">
            <Spinner size="sm" />
            <Text size="sm" color="muted">Loading effective steps...</Text>
          </VStack>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header title={tenant.name} />
      <Card.Content>
        {steps.length === 0 ? (
          <Text size="sm" color="muted" className="py-4 text-center">
            No steps returned for this tenant.
          </Text>
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Order</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Loop</Table.HeaderCell>
                <Table.HeaderCell>Mode</Table.HeaderCell>
                <Table.HeaderCell>Active</Table.HeaderCell>
                <Table.HeaderCell>Timeout</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {steps.map((step) => (
                <Table.Row key={step.id}>
                  <StepRow step={step} genericStepIds={genericStepIds} />
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Card.Content>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface WorkflowStepsListPanelProps {
  readonly workflowSlug: string;
}

export function WorkflowStepsListPanel({ workflowSlug }: WorkflowStepsListPanelProps) {
  const navigate = useNavigate();

  const listPage = useAdminListPage<WorkflowStep, string>({
    queryKey: ['admin', 'workflows', workflowSlug, 'steps'],
    queryFn: () => workflowStepsService.list(workflowSlug),
    deleteFn: (stepId) => workflowStepsService.remove(workflowSlug, stepId),
    getDeleteId: (step) => step.id,
    extraInvalidateKeys: [
      ['admin', 'workflows', workflowSlug],
    ],
    transformData: (items) => [...items].sort((a, b) => a.sort_order - b.sort_order),
  });

  // Fetch tenants that have rules on this workflow
  const { data: tenantsResponse } = useQuery({
    queryKey: ['admin', 'workflows', workflowSlug, 'rules', 'tenants'],
    queryFn: () => workflowRulesService.getTenants(workflowSlug),
  });
  const tenants = tenantsResponse?.data ?? [];

  // Set of generic step IDs for diffing against effective steps
  const genericStepIds = useMemo(
    () => new Set(listPage.items.map((s) => s.id)),
    [listPage.items],
  );

  return (
    <VStack gap={6}>
      {/* Generic workflow steps */}
      <AdminListPage<WorkflowStep>
        variant="panel"
        title="Steps"
        subtitle={tenants.length > 0 ? 'Generic workflow (before tenant rules)' : undefined}
        loadingLabel="Loading steps..."
        errorTitle="Failed to load steps"
        errorFallback="An unexpected error occurred."
        emptyTitle="No steps found"
        emptyDescription="Get started by creating your first step for this workflow."
        createLabel="Create Step"
        createButtonSize="sm"
        onCreateClick={() => navigate(`/admin/workflows/${workflowSlug}/steps/create`)}
        items={listPage.items}
        getRowKey={(step) => step.id}
        isLoading={listPage.isLoading}
        error={listPage.error}
        deleteModalTitle="Delete Step"
        deleteTarget={listPage.deleteTarget}
        getDeleteTargetName={(step) => step.name}
        deleteNoun="step"
        onDeleteCancel={listPage.cancelDelete}
        onDeleteConfirm={listPage.confirmDelete}
        isDeleting={listPage.isDeleting}
        isDeleteError={listPage.isDeleteError}
        deleteError={listPage.deleteError}
        renderHead={() => (
          <>
            <Table.HeaderCell>Order</Table.HeaderCell>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Type</Table.HeaderCell>
            <Table.HeaderCell>Loop</Table.HeaderCell>
            <Table.HeaderCell>Mode</Table.HeaderCell>
            <Table.HeaderCell>Active</Table.HeaderCell>
            <Table.HeaderCell>Timeout</Table.HeaderCell>
            <Table.HeaderCell align="right">Actions</Table.HeaderCell>
          </>
        )}
        renderRow={(step) => {
          const iterateGroup = step.config?.iterate_group as string | undefined;
          return (
          <>
            <Table.Cell>
              <Text size="sm" color="muted">
                {step.sort_order}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Text weight="medium">{step.name}</Text>
            </Table.Cell>
            <Table.Cell>
              <Badge variant="info" size="sm">
                {step.step_type}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              {iterateGroup ? (
                <Badge variant="warning" size="sm">
                  {iterateGroup}
                </Badge>
              ) : (
                <Text size="sm" color="muted">{'\u2014'}</Text>
              )}
            </Table.Cell>
            <Table.Cell>
              <Badge
                variant={step.execution_mode === 'async' ? 'warning' : 'default'}
                size="sm"
              >
                {step.execution_mode}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Badge
                variant={step.is_active ? 'success' : 'default'}
                size="sm"
              >
                {step.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Text size="sm" color="muted">
                {step.timeout_seconds == null ? '\u2014' : `${step.timeout_seconds}s`}
              </Text>
            </Table.Cell>
            <Table.Cell align="right">
              <HStack gap={1} justify="end">
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Icon icon={Pencil} size="xs" color="inherit" />}
                  onClick={() => navigate(`/admin/workflows/${workflowSlug}/steps/${step.id}/edit`)}
                  aria-label={`Edit ${step.name}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Icon icon={Trash2} size="xs" />}
                  onClick={() => listPage.setDeleteTarget(step)}
                  aria-label={`Delete ${step.name}`}
                />
              </HStack>
            </Table.Cell>
          </>
          );
        }}
      />

      {/* Tenant-specific effective steps */}
      {tenants.length > 0 && (
        <VStack gap={4}>
          <Text as="h3" size="lg" weight="semibold">
            Tenant-specific workflows
          </Text>
          {tenants.map((tenant) => (
            <TenantEffectiveStepsTable
              key={tenant.id}
              workflowSlug={workflowSlug}
              tenant={tenant}
              genericStepIds={genericStepIds}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
}
